import { useQuery } from '@tanstack/react-query';
import { fetchEpicDiff } from '../../../../lib/api/epics';
import { RepoDiffBlock } from '../diff/EpicDiffSection';
import React, { useState } from 'react';
import type { EpicDetail, StorySpec, StorySummary } from '../../../../../../shared/types/epics';
import { StatusBadge, toneForEpicState, labelForEpicState } from '../../../../components/ui';

// ---------------------------------------------------------------------------
// Expanded row of the Plan tab's story table: everything the planner wrote for
// the story (description, acceptance criteria, non-goals, implementation notes)
// plus where it runs, what it depends on and how it is going.
// ---------------------------------------------------------------------------

export interface SpecSection { title: string; text: string }

const KNOWN = /^(description|acceptance criteria|non-?goals?|implementation notes?|impl(?:ementation)? notes?|notes?)\s*:?$/i;

/** Split the planner's markdown body into its labelled sections. Pure. */
export function splitSpecSections(body: string): SpecSection[] {
  const out: SpecSection[] = [];
  let cur: SpecSection | null = null;
  for (const line of body.replace(/\r\n/g, '\n').split('\n')) {
    const h = /^\s*(?:#{1,4}\s+|\*\*)(.+?)(?:\*\*)?\s*:?\s*$/.exec(line);
    const title = h ? h[1].replace(/\*+/g, '').trim() : null;
    if (title && KNOWN.test(title)) {
      cur = { title, text: '' };
      out.push(cur);
    } else if (cur) {
      cur.text += `${line}\n`;
    } else if (line.trim()) {
      cur = { title: 'Description', text: `${line}\n` };
      out.push(cur);
    }
  }
  return out.map((s) => ({ ...s, text: s.text.trim() })).filter((s) => s.text);
}

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</dt>
    <dd className="text-sm text-slate-800 mt-0.5">{children}</dd>
  </div>
);

/** What this story changed — the commit range Forge recorded at merge time. */
const StoryChanges: React.FC<{ epicId: string; storyId: string }> = ({ epicId, storyId }) => {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['epic', 'story-diff', epicId, storyId],
    queryFn: () => fetchEpicDiff(epicId, storyId),
    enabled: open,
    retry: false,
    staleTime: 60_000,
  });
  return (
    <section data-testid="story-changes">
      <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setOpen((o) => !o)} data-testid="story-changes-toggle">
        {open ? '▾ Hide changes' : '▸ Show what this story changed'}
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {q.isLoading && <div className="text-xs text-slate-500">Loading changes…</div>}
          {q.isError && (
            <div className="text-xs text-slate-500 italic" data-testid="story-changes-empty">{(q.error as Error).message}</div>
          )}
          {q.data?.repos.map((rd) => <RepoDiffBlock key={rd.repo} rd={rd} multi={q.data.repos.length > 1} />)}
        </div>
      )}
    </section>
  );
};

export const StoryDetail: React.FC<{
  epic: EpicDetail;
  story: StorySummary;
  spec?: StorySpec;
  planLoading?: boolean;
}> = ({ epic, story, spec, planLoading }) => {
  const sections = spec?.body ? splitSpecSections(spec.body) : [];
  const [org, repo] = epic.repo.split('/');
  const issueUrl = story.githubIssueNumber && org && repo
    ? `https://github.com/${org}/${repo}/issues/${story.githubIssueNumber}`
    : null;
  const fa = story.failureAnalysis;

  return (
    <div className="px-4 py-3 bg-slate-50 space-y-3" data-testid={`story-detail-${story.id}`}>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Fact label="Status">
          <StatusBadge tone={toneForEpicState(story.state)}>{labelForEpicState(story.state)}</StatusBadge>
        </Fact>
        <Fact label="Target repo">
          <span className="font-mono text-xs">{spec?.targetRepo ?? epic.repo}</span>
        </Fact>
        <Fact label="Size">{spec?.size ?? '—'}</Fact>
        <Fact label="Agent runs">{story.sessionCount}</Fact>
        {spec && spec.dependsOn.length > 0 && (
          <Fact label="Depends on">{spec.dependsOn.join(', ')}</Fact>
        )}
        {spec && spec.scope.length > 0 && (
          <Fact label="Scope"><span className="font-mono text-xs">{spec.scope.join(', ')}</span></Fact>
        )}
        {issueUrl && (
          <Fact label="GitHub">
            <a href={issueUrl} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
              #{story.githubIssueNumber} ↗
            </a>
          </Fact>
        )}
      </dl>

      {fa && (fa.stallReason || fa.exitReason || fa.retryAdvice) && (
        <div className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2" data-testid="story-detail-failure">
          <div className="font-medium">Why it stopped</div>
          <div className="whitespace-pre-wrap text-xs mt-0.5">
            {fa.stallReason ?? fa.exitReason ?? 'A retry-analyst brief is on the Progress tab.'}
          </div>
        </div>
      )}

      <StoryChanges epicId={String(epic.id)} storyId={story.id} />

      {planLoading && <div className="text-xs text-slate-500">Loading the planner's spec…</div>}
      {!planLoading && sections.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="story-detail-nospec">
          No stored spec for this story (the epic predates plan storage, or it hasn't been approved yet).
        </div>
      )}
      {sections.map((s) => (
        <section key={s.title} data-testid="story-detail-section">
          <h4 className="text-xs font-semibold text-slate-700 mb-1">{s.title}</h4>
          <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-700 bg-white border border-slate-200 rounded-md p-3 font-sans">
            {s.text}
          </pre>
        </section>
      ))}
    </div>
  );
};
